/**
 * @file FollowupForm.jsx
 * @author Rishikesh
 * @date 2024-09-25
 * @description Component of lead-details to manage the followup form.
 */
import React from "react";
import {
  Box,
  Divider,
  Skeleton,
  Stack,
  TextField,
  Typography,
  Button,
  MenuItem,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Controller } from "react-hook-form";

export const FollowupForm = ({
  isLoading,
  followupTypes,
  register,
  errors,
  watch,
  setValue,
  control,
  onSubmit,
  followuplist,
  today,
}) => {
  return (
    <Box
      width="100%"
      display="flex"
      flexDirection="column"
      rowGap={1}
      overflow="hidden"
    >
      <Box
        width="100%"
        borderRadius="12px"
        px="16px"
        py="8px"
        bgcolor="#F7F7F7"
        display="flex"
        flexDirection="column"
        border="1px solid #E5E5E5"
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          {isLoading ? (
            <Skeleton variant="rounded" width="50%" height={40} />
          ) : (
            <Controller
              name="followupdate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  sx={{
                    "& .MuiInputBase-root": {
                      width: "auto",
                      height: 42,
                      padding: "4px 8px",
                      borderRadius: "12px",
                    },
                    "& .MuiOutlinedInput-root": {
                      m: 0,
                      height: 42,
                    },
                    "& .MuiInputBase-input": {
                      padding: "0 8px",
                      height: "auto",
                    },
                  }}
                  disabled
                  format="ddd MMM DD YYYY"
                />
              )}
            />
          )}

          {isLoading ? (
            <Skeleton variant="rounded" width="30%" height={40} />
          ) : (
            <TextField
              sx={{
                "& .MuiInputBase-input": {
                  m: 0,
                  p: "4px 8px",
                  minWidth: 120,
                },
                "& .MuiOutlinedInput-root": {
                  mt: 0,
                  borderRadius: "12px",
                  height: 42,
                },
                width: "auto",
              }}
              defaultValue=""
              value={watch("followuptype")}
              onChange={(e) => setValue("followuptype", e.target.value)}
              select
              variant="outlined"
              placeholder="Select"
              size="small"
            >
              {followupTypes.map((option) => (
                <MenuItem key={option.followuptype} value={option.followuptype}>
                  {option.followuptype}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
        {isLoading ? (
          <Skeleton variant="rounded" width="100%" height={50} />
        ) : (
          <TextField
            disabled
            id="standard-multiline-static"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                bgcolor: "#EDEDED",
                fontSize: "14px",
              },
            }}
            placeholder="Notes"
            maxRows={3}
            multiline
            {...register("followupnotes")}
            defaultValue="Consequatur iusto qui veniam fugiat culpa iste est cupiditate."
            variant="outlined"
          />
        )}
        {!isLoading &&
          (followuplist?.length === 1 ? (
            <Typography
              variant="caption"
              mt={0.4}
              fontStyle="italic"
              textAlign="right"
              color="text.secondary"
            >
              Note: System generated
            </Typography>
          ) : (
            <Stack my={1} />
          ))}
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 2 }}
            width="100%"
            height={100}
          />
        ) : (
          <TextField
            id="standard-multiline-static"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
            }}
            placeholder="Outcome"
            multiline
            rows={3}
            variant="outlined"
            slotProps={{ htmlInput: { maxLength: 500 } }}
            error={!!errors.followupoutcome}
            helperText={
              errors.followupoutcome ? errors.followupoutcome.message : ""
            }
            {...register("followupoutcome", {
              required: "Outcome is required",
            })}
          />
        )}
      </Box>

      <Divider />

      <Box
        flex={1}
        borderRadius="12px"
        px="16px"
        display="flex"
        flexDirection="column"
      >
        {isLoading ? (
          <Skeleton sx={{ my: 1 }} variant="rounded" width={180} height={18} />
        ) : (
          <Typography fontWeight={600} fontSize={18} color="#3A80F7">
            Next Follow-up
          </Typography>
        )}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          {isLoading ? (
            <Skeleton variant="rounded" width="50%" height={40} />
          ) : (
            <Controller
              name="nextfollowupdate"
              control={control}
              rules={{ required: "Next follow-up date is required" }}
              render={({ field }) => (
                <FormControl
                  error={!!errors.nextfollowupdate}
                  sx={{ width: "auto" }}
                >
                  <DatePicker
                    {...field}
                    sx={{
                      "& .MuiInputBase-root": {
                        width: "auto",
                        height: 42,
                        padding: "4px 8px",
                        borderRadius: "12px",
                      },
                      "& .MuiOutlinedInput-root": {
                        m: 0,
                        height: 42,
                      },
                      "& .MuiInputBase-input": {
                        padding: "0 8px",
                        height: "auto",
                      },
                    }}
                    minDate={today}
                    format="ddd MMM DD YYYY"
                  />
                  <FormHelperText>
                    {errors.nextfollowupdate
                      ? errors.nextfollowupdate.message
                      : ""}
                  </FormHelperText>
                </FormControl>
              )}
            />
          )}

          {isLoading ? (
            <Skeleton variant="rounded" width="30%" height={40} />
          ) : (
            <TextField
              sx={{
                "& .MuiInputBase-input": {
                  m: 0,
                  p: "4px 8px",
                  minWidth: 120,
                },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  height: 42,
                },
                width: "auto",
              }}
              value={watch("nextfollowuptype")}
              onChange={(e) => setValue("nextfollowuptype", e.target.value)}
              defaultValue=""
              select
              variant="outlined"
              placeholder="Select"
              size="small"
              error={!!errors.nextfollowuptype}
              helperText={
                errors.nextfollowuptype ? errors.nextfollowuptype.message : ""
              }
              {...register("nextfollowuptype", {
                required: "Type is required",
              })}
            >
              {followupTypes.map((option) => (
                <MenuItem key={option.followuptype} value={option.followuptype}>
                  {option.followuptype}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>

        {isLoading ? (
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ mt: 1 }}
          />
        ) : (
          <TextField
            id="standard-multiline-static"
            sx={{
              "& .MuiOutlinedInput-root": {
                mt: 1,
                borderRadius: "12px",
              },
            }}
            placeholder="Notes"
            multiline
            slotProps={{ htmlInput: { maxLength: 500 } }}
            error={!!errors.nextfollowupnotes}
            helperText={
              errors.nextfollowupnotes ? errors.nextfollowupnotes.message : ""
            }
            {...register("nextfollowupnotes", {
              required: "Notes is required",
            })}
            maxRows={2}
            variant="outlined"
          />
        )}
        {isLoading ? (
          <Skeleton
            variant="rounded"
            width={80}
            height={36}
            sx={{ mt: 1, alignSelf: "flex-end" }}
          />
        ) : (
          <Button
            variant="contained"
            sx={{ borderRadius: "12px", mt: 1, px: 4, alignSelf: "flex-end" }}
            onClick={onSubmit}
          >
            Save
          </Button>
        )}
      </Box>
    </Box>
  );
};
